import type { ThinClient } from '../types';

interface ThinClientDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: ThinClient | null;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

export default function ThinClientDetailModal({ 
  isOpen, 
  onClose, 
  client,
  onEdit,
  onToggleStatus,
  onDelete
}: ThinClientDetailModalProps) {
  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">硬件终端详情</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-gray-500 mb-1">ID</div>
                <div className="text-sm text-gray-900">{client.id}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">终端别名</div>
                <div className="text-sm text-gray-900">{client.alias}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">序列号SN</div>
                <div className="text-sm text-gray-900">{client.serialNumber}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">状态</div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  client.status === 'enabled' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {client.status === 'enabled' ? '启用' : '停用'}
                </span>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">场所</div>
                <div className="text-sm text-gray-900">{client.venue}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">租户</div>
                <div className="text-sm text-gray-900">{client.tenant}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">无线MAC</div>
                <div className="text-sm text-gray-900">{client.macWireless}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">有线MAC</div>
                <div className="text-sm text-gray-900">{client.macWired}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">固件版本</div>
                <div className="text-sm text-gray-900">{client.firmwareVersion}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">最近开机时间</div>
                <div className="text-sm text-gray-900">{client.lastBootTime}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">当前登录用户</div>
                <div className="text-sm text-gray-900">{client.currentUser || '-'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">最近登录用户</div>
                <div className="text-sm text-gray-900">{client.lastUser || '-'}</div>
              </div>
              {client.currentHost && (
                <div className="col-span-2">
                  <div className="text-sm text-gray-500 mb-1">当前连接主机</div>
                  <div className="text-sm text-gray-900">
                    {client.currentHost.id} - {client.currentHost.name}
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm text-gray-500 mb-1">创建时间</div>
                <div className="text-sm text-gray-900">{client.createdAt}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">创建人</div>
                <div className="text-sm text-gray-900">{client.createdBy}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">更新时间</div>
                <div className="text-sm text-gray-900">{client.updatedAt}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">更新人</div>
                <div className="text-sm text-gray-900">{client.updatedBy}</div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
            <button
              onClick={onEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              编辑
            </button>
            <button
              onClick={onToggleStatus}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                client.status === 'enabled'
                  ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  : 'text-white bg-green-600 hover:bg-green-700'
              }`}
            >
              {client.status === 'enabled' ? '停用' : '启用'}
            </button>
            <button
              onClick={onDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              删除
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              关闭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
